/**
 * The least audio worth sending through the pipeline.
 *
 * A therapy session is fifty minutes; ten is the point below which what was
 * captured is almost certainly not one — a test, a false start, a call that
 * dropped before anyone settled. Analysing those produces confident clinical
 * writing about nothing, which is worse than producing nothing at all, and
 * costs a transcription either way.
 *
 * One number, shared by every route in: recording live, capturing a call,
 * uploading a file, holding an AlchelyMeeting. The backend holds its own copy
 * for the meeting path, where duration is only known once LiveKit reports it.
 */
export const MIN_SESSION_AUDIO_SECONDS = 10 * 60;

export const MIN_SESSION_AUDIO_MINUTES = MIN_SESSION_AUDIO_SECONDS / 60;

/** `m:ss`, for a countdown the therapist reads at a glance. */
export const formatDuration = (seconds: number): string => {
    const whole = Math.max(0, Math.round(seconds));
    return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
};

/**
 * How long an audio file runs, or null when the browser cannot tell.
 *
 * Reads the metadata only — nothing is decoded and the bytes never leave the
 * machine. A null is not a rejection: a file the browser cannot measure may
 * still be a perfectly good recording, and refusing it on that basis would
 * block a real session over a codec quirk.
 */
export const audioDurationOf = (file: File): Promise<number | null> =>
    new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const audio = new Audio();

        const done = (seconds: number | null) => {
            URL.revokeObjectURL(url);
            resolve(seconds);
        };

        audio.preload = "metadata";
        audio.onloadedmetadata = () =>
            done(Number.isFinite(audio.duration) ? audio.duration : null);
        audio.onerror = () => done(null);
        audio.src = url;
    });
